import { Button, Icon } from '@/components/common';
import useModal from '@/hooks/useModal';
import { FieldConfig, SendDocumentPayload } from '@/interfaces';
import * as React from 'react';
import clsx from 'clsx';
import Icons from '@/const/icons';
import { FieldValues, useForm } from 'react-hook-form';
import useModalPreviewPage from '@/hooks/useModalPreviewPage';
import { useFilters, useNotification } from '@/hooks';
import { DocumentPreviewModal } from './DocumentPreviewModal';
import { caseDocumentsService } from '@/api/caseDocuments';
import { formatISO, parse } from 'date-fns';
import SendDocumentForm from './SendDocumentForm';
import { ModalHeaderArrowBack } from '@/components/modals/ModalHeaderArrowBack';
import { useCaseClientFilters } from '@/hooks/useCaseClientFilters';
import { useQueryClient } from '@tanstack/react-query';
import { ConfigureFieldsStep } from './configure-fields';
import { countPdfFields } from './configure-fields/lib/countPdfFields';
import {
	awsService,
	GET_CASE_CLIENTS_DOCUMENTS,
	GET_CLIENT_DIRECTORY,
	GET_CLIENT_PROFILE_ACTIONS_REQUIRED,
	GET_CLIENT_PROFILE_FILES,
} from '@/api';

export const SendDocument: React.FC<{
	caseId: string;
	caseName: string;
	open?: boolean;
	setOpen?: (open: boolean) => void;
	userIds?: string[];
	onBack?: () => void;
	setSelectedUserIds: React.Dispatch<React.SetStateAction<(string | null)[]>>;
}> = ({
	caseId,
	caseName,
	open = false,
	setOpen,
	userIds,
	onBack,
	setSelectedUserIds,
}) => {
	const queryClient = useQueryClient();
	const { parsedFilters } = useCaseClientFilters();
	const { removeParaFilter } = useFilters();
	const { toastError } = useNotification();
	const [file, setFile] = React.useState<File | null>(null);
	const [clientsIdSelected, setClientsIdSelected] = React.useState<Set<string>>(
		new Set()
	);
	const [isLoading, setIsLoading] = React.useState(false);
	const [step, setStep] = React.useState<
		'upload' | 'configure-fields' | 'email'
	>('upload');
	const [pdfUrl, setPdfUrl] = React.useState<string | null>(null);
	const [fieldConfigs, setFieldConfigs] = React.useState<
		Record<string, FieldConfig>
	>({});
	const [isConfigureOverlayActive, setIsConfigureOverlayActive] =
		React.useState(false);

	const {
		register,
		handleSubmit,
		watch,
		formState,
		reset,
		setValue,
		getValues,
	} = useForm({ mode: 'onSubmit' });

	const { ModalPreviewPage, hidePreviewPage, showPreviewPage } =
		useModalPreviewPage();

	const {
		Modal: ModalUploadDocument,
		show: showModalUploadDocument,
		hide: hideModalUploadDocument,
	} = useModal();

	const {
		Modal: ModalDocumentSent,
		show: showModalDocumentSent,
		hide: hideModalDocumentSent,
	} = useModal();

	React.useEffect(() => {
		if (open) {
			handleClickSelectSend();
		}
	}, [open]);

	React.useEffect(() => {
		if (step !== 'configure-fields' || !file) {
			return;
		}

		const objectUrl = URL.createObjectURL(file);
		setPdfUrl(objectUrl);

		return () => {
			URL.revokeObjectURL(objectUrl);
			setPdfUrl(null);
		};
	}, [step, file]);

	const onClickSendDocument = (): void => {
		showModalUploadDocument();
	};

	const handleBack = (): void => {
		if (step === 'email') {
			setStep('configure-fields');
		} else if (step === 'configure-fields') {
			setStep('upload');
		} else if (onBack) {
			onBack();
			setOpen?.(false);
			hideModalUploadDocument();
		} else {
			hideModalUploadDocument();
		}
	};

	const [isCheckingFields, setIsCheckingFields] = React.useState(false);
	const [hasFieldsHint, setHasFieldsHint] = React.useState<boolean>();

	const handleContinue = async () => {
		if (!file) return;
		setIsCheckingFields(true);
		const fieldCount = await countPdfFields(file);
		setIsCheckingFields(false);
		setHasFieldsHint(fieldCount === null ? undefined : fieldCount > 0);
		setStep('configure-fields');
	};

	// Configs are tied to the current PDF's field ids; a new file starts clean.
	React.useEffect(() => {
		setFieldConfigs({});
		setHasFieldsHint(undefined);
	}, [file]);

	const handleContinueFromFields = (
		configs: Record<string, FieldConfig>
	): void => {
		setFieldConfigs(configs);
		setStep('email');
	};

	const handleSendDocument = async (data: FieldValues): Promise<void> => {
		if (!file) {
			toastError('Please upload the document');
			return;
		}

		setIsLoading(true);
		const users_ids = userIds ?? Array.from(clientsIdSelected);

		try {
			const date = data.sign_by_date
				? parse(data.sign_by_date.replace(/\//g, '-'), 'MM-dd-yyyy', new Date())
				: null;
			const dateISO = date ? formatISO(date) : undefined;

			const fileName = data.document_name.endsWith('.pdf')
				? data.document_name
				: `${data.document_name}.pdf`;

			const pdfFile = new File([file], fileName, {
				type: 'application/pdf',
			});
			console.log({ pdfFile });
			const url = (await awsService.uploadImage(pdfFile)).data.data.location;

			const required_questions = Object.values(fieldConfigs).map((config) => ({
				field_name: config.fieldName,
				question: config.renamedLabel,
				type: config.dataType,
			}));

			const newData: SendDocumentPayload = {
				case_id: caseId,
				users_ids: users_ids,
				doc_url: url,
				document_name: data.document_name,
				sign_date: dateISO,
				is_signature_required: Object.values(fieldConfigs).some(
					(config) => config.dataType === 'SIGNATURE'
				),
				required_questions,
				hideSigningDate: false,
				...parsedFilters,
			};

			if (data.message) {
				newData.email_text = data.message.replaceAll('\n', '<br/>');
			}

			await caseDocumentsService.postSendDocument(newData);
			queryClient.invalidateQueries({
				queryKey: [GET_CLIENT_PROFILE_FILES],
			});
			queryClient.invalidateQueries({
				queryKey: [GET_CLIENT_PROFILE_ACTIONS_REQUIRED],
			});
			queryClient.invalidateQueries({ queryKey: [GET_CLIENT_DIRECTORY] });
			queryClient.invalidateQueries({ queryKey: [GET_CASE_CLIENTS_DOCUMENTS] });

			hidePreviewPage();
			showModalDocumentSent();
			setSelectedUserIds([]);
			handleClose();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setIsLoading(false);
		}
	};

	const handleApprove = (): void => {
		const dataCollected = getValues();
		handleSendDocument(dataCollected);
	};

	const handleClickSelectSend = (): void => {
		reset();
		setFile(null);
		setFieldConfigs({});
		setClientsIdSelected(new Set());
		onClickSendDocument();
	};

	const handleClose = (): void => {
		setOpen?.(false);
		removeParaFilter('search');
		setClientsIdSelected(new Set());
		setFile(null);
		setFieldConfigs({});
		reset();
		hideModalUploadDocument();
		setStep('upload');
	};

	const isConfigureFieldsStep = step === 'configure-fields';

	return (
		<>
			<ModalUploadDocument
				title={
					isConfigureFieldsStep ? undefined : (
						<ModalHeaderArrowBack
							title={`${
								step === 'email' ? 'Add Email Language' : 'Configure Document'
							}`}
							handleClose={handleClose}
							handleBack={handleBack}
							showBack={step === 'email' || !!onBack}
						/>
					)
				}
				backgroundColor="white"
				transparentBackground={
					isConfigureFieldsStep && isConfigureOverlayActive
				}
				classNameCardContainer={clsx(
					'flex flex-col !transition-[opacity,transform]',
					{
						'send-document-modal-panel': !isConfigureFieldsStep,
						'!p-0 !shadow-none !bg-transparent !rounded-none w-auto':
							isConfigureFieldsStep,
					}
				)}
				classNameContainerChildren={
					isConfigureFieldsStep
						? '!px-0 w-auto min-h-0'
						: 'w-full md:w-[520px] min-h-0'
				}
				noMaxHeight
				avoidClose
				className={isConfigureFieldsStep ? undefined : 'overflow-y-hidden'}
			>
				<style>{`
					.send-document-modal-panel {
						max-height: 93vh;
					}
					@supports (height: 100dvh) {
						.send-document-modal-panel {
							max-height: 93dvh;
						}
					}
				`}</style>
				{isConfigureFieldsStep ? (
					file && pdfUrl ? (
						<ConfigureFieldsStep
							pdfUrl={pdfUrl}
							file={file}
							onBack={() => setStep('upload')}
							onClose={handleClose}
							onContinue={handleContinueFromFields}
							initialFieldConfigs={fieldConfigs}
							hasFieldsHint={hasFieldsHint}
							onOverlayActiveChange={setIsConfigureOverlayActive}
						/>
					) : null
				) : (
					<form onSubmit={handleSubmit(handleSendDocument)} className="h-full">
						<SendDocumentForm
							isLoading={isLoading || isCheckingFields}
							file={file}
							register={register}
							setFile={setFile}
							showPreviewPage={() => {
								hideModalUploadDocument();
								showPreviewPage();
							}}
							formState={formState}
							hideModalUploadDocument={hideModalUploadDocument}
							handleContinue={handleContinue}
							setValue={setValue}
							watch={watch}
							showMessageInput={step === 'email'}
						/>
					</form>
				)}
			</ModalUploadDocument>
			<ModalDocumentSent
				withXButton
				title={'Document sent'}
				classNameTitle="!px-8 text-center"
				classNameContainerChildren="w-full md:w-[532px]"
				backgroundColor="white"
			>
				<div className="flex flex-col items-center justify-center">
					<Icon src={Icons.checkCircleEmptyState} width={150} height={150} />
					<Button
						label="Close"
						size="fit"
						decoration="secondary"
						onClick={hideModalDocumentSent}
					/>
				</div>
			</ModalDocumentSent>
			<ModalPreviewPage
				handleClickApprove={handleApprove}
				handleClickBack={() => {
					showModalUploadDocument();
					hidePreviewPage();
				}}
				hideEditButton={true}
				hideTitle={true}
				ariaLabel="Email preview"
				approveCustomLabel="Send Document"
			>
				<DocumentPreviewModal
					descriptionText={watch('message')}
					caseName={caseName}
					watch={watch}
				/>
			</ModalPreviewPage>
		</>
	);
};
